/**
 * Keeps an upload screen's files and metadata alive across unmounts.
 *
 * Saves are debounced because the upload screens rewrite their `fileLogs` array on
 * every keystroke and every picker selection; writing each of those straight to
 * IndexedDB would put a transaction behind every character typed.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  clearUploadDraft,
  loadUploadDraft,
  saveUploadDraftFiles,
  saveUploadDraftMeta,
} from "shared/utils/uploadDraftStore";

const SAVE_DEBOUNCE_MS = 800;

interface UseUploadDraftOptions<TMeta> {
  /** Namespaces the draft, e.g. `upload-files:pictures`. */
  key: string;
  /** Current files; an empty array clears the stored draft. */
  files: File[];
  /** Serializable metadata to restore alongside the files. */
  meta: TMeta;
  /**
   * Set false to disable persistence entirely (e.g. while a batch is uploading,
   * so a half-drained queue is not what gets restored).
   */
  enabled?: boolean;
  /**
   * Called once on mount when a usable draft exists. Returning nothing is fine;
   * the hook only reports that a draft was found.
   */
  onRestore?: (draft: { files: File[]; meta: TMeta; savedAt: number }) => void;
}

export const useUploadDraft = <TMeta>({
  key,
  files,
  meta,
  enabled = true,
  onRestore,
}: UseUploadDraftOptions<TMeta>) => {
  const [hasRestored, setHasRestored] = useState(false);

  // The restore callback is intentionally not a dependency: it closes over screen
  // state and would otherwise re-run the one-shot restore on every render.
  const onRestoreRef = useRef(onRestore);
  useEffect(() => {
    onRestoreRef.current = onRestore;
  }, [onRestore]);

  const metaRef = useRef(meta);
  metaRef.current = meta;

  // Guards against the debounced save firing before the restore has landed, which
  // would persist the empty initial state over a perfectly good draft.
  //
  // This is state rather than a ref on purpose: the save effect has to re-run once
  // the restore completes, otherwise files added during the (short) async window
  // would not be persisted until the next unrelated change.
  const [isRestoreDone, setIsRestoreDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setIsRestoreDone(true);
      return;
    }

    loadUploadDraft<TMeta>(key).then((draft) => {
      if (cancelled) {
        return;
      }
      if (draft) {
        onRestoreRef.current?.({
          files: draft.files,
          meta: draft.meta,
          savedAt: draft.savedAt,
        });
        setHasRestored(true);
      }
      // Batched with the restore above, so the next render already sees the
      // restored files - the save effect cannot mistake them for an empty screen.
      setIsRestoreDone(true);
    });

    return () => {
      cancelled = true;
    };
  }, [key, enabled]);

  // Identity of the file list as last persisted. Writing blobs is orders of
  // magnitude more expensive than writing metadata, so it happens only when the
  // set of files genuinely changed - not on every label or date edit.
  const persistedFilesRef = useRef<readonly File[] | null>(null);

  useEffect(() => {
    if (!enabled || !isRestoreDone) return;

    const timer = setTimeout(() => {
      if (files.length === 0) {
        persistedFilesRef.current = null;
        void clearUploadDraft(key);
        return;
      }

      const previous = persistedFilesRef.current;
      const filesChanged =
        !previous ||
        previous.length !== files.length ||
        files.some((file, index) => previous[index] !== file);

      if (filesChanged) {
        persistedFilesRef.current = files;
        void saveUploadDraftFiles(key, files);
      }

      void saveUploadDraftMeta(key, metaRef.current);
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // `meta` is read through a ref at save time, but is still a dependency so that
    // metadata-only edits (a label, a date) also restart the debounce window.
  }, [key, enabled, isRestoreDone, files, meta]);

  const clear = useCallback(() => {
    setHasRestored(false);
    persistedFilesRef.current = null;
    return clearUploadDraft(key);
  }, [key]);

  return { hasRestoredDraft: hasRestored, clearDraft: clear };
};
